"use client"
import { Box, Button, Flex, Grid, Paper, TextInput, Title } from "@mantine/core";
import { COLORS } from "src/constants";
import { Categories, getCategory } from "src/entities/categories";
import { Products, ProductsFilter, searchProducts } from "src/entities/products";
import { ProductCard } from "src/features/client/product/ui/product-item";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Content } from "src/shared/helpers/content";
import { useParams } from "react-router-dom";
import { IconSearch } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { generateMetaData } from "src/shared/config/seo/meta-data";
import { Helmet } from "react-helmet";


type Props = {
    page?: "main" | "products" | "productDetail"
    categoryIdLikeProp?: number
}
export const ProductsWidget = ({ page = "products", categoryIdLikeProp }: Props) => {
    const isMainpage = page === "main"
    const isProductsPage = page === "products"
    const isProductDetailPage = page === "productDetail"

    const { t, i18n } = useTranslation()
    const locale = i18n.language
    const [category, setCategory] = useState<Categories | undefined>()
    const [products, setProducts] = useState<SearchResponse<Products> | undefined>()
    const { categoryId } = useParams<{ categoryId: string }>()

    const fetchCategory = async () => {
        if (categoryId) {
            const res = await getCategory(categoryId as string, false)
            setCategory(res)
        }
    }

    const productsComponents = products?.content?.map((product: Products) => (
        <Grid.Col key={product.id} span={{ base: 6, md: 4, xl: 3 }}>
            <ProductCard data={product} />
        </Grid.Col>
    ))
    const form = useForm<ProductsFilter>({
        initialValues: {
            title: "",
        }
    })

    const handleClickSearch = async (values?: ProductsFilter) => {
        const res = await searchProducts({
            filter: {
                title: values?.title,
                content: values?.content,
                categoryId: values?.categoryId,
            },
            sorting: { sortBy: "ID", sortDirection: "DESC" },
            pageRequest: {
                limit: isMainpage ? 4 : 100,
                page: 0,
            }
        }, false)
        setProducts(res)
    }

    useEffect(() => {

        const fetchAll = async () => {
            await fetchCategory();
            await handleClickSearch({ categoryId: categoryIdLikeProp ? categoryIdLikeProp : categoryId ? parseInt(`${categoryId}`) : undefined, })
        };
        fetchAll();
    }, [categoryId]);

    const metaData = generateMetaData(
        category?.titleRu || "",
        category?.contentRu || "",
        `/category/${categoryId}`
    );

    return (
        <Box w="100%" py={isMainpage ? 0 : 40}>
            <Helmet>
                <title>{metaData.title}</title>
                {metaData.description && <meta name="description" content={metaData.description} />}
                {metaData.openGraph && (
                    <meta property="og:title" content={metaData.openGraph.title} />
                )}
                {metaData.openGraph && (
                    <meta property="og:description" content={metaData.openGraph.description} />
                )}
                {metaData.openGraph && (
                    <meta property="og:image" content={metaData.openGraph.images} />
                )}
                {metaData.openGraph && (
                    <meta property="og:url" content={metaData.openGraph.url} />
                )}
                {metaData.twitter && (
                    <meta name="twitter:card" content={metaData.twitter.card} />
                )}
                {metaData.twitter && (
                    <meta name="twitter:title" content={metaData.twitter.title} />
                )}
                {metaData.twitter && (
                    <meta name="twitter:description" content={metaData.twitter.description} />
                )}
                {metaData.twitter && (
                    <meta name="twitter:image" content={metaData.twitter.images} />
                )}
            </Helmet>
            <Paper shadow="lg" style={{ borderRadius: "16px" }} mb={28} p="12px 16px">
                <form onSubmit={form.onSubmit(handleClickSearch)}>
                    <Flex align="center" gap={10}>
                        <TextInput
                            {...form.getInputProps("title")}
                            styles={{
                                input: {
                                    border: "none"
                                }
                            }}
                            fz={16} fw={500}
                            c="#6B7583"
                            placeholder={t("form.label.searchText")}
                            w="100%" />
                        <Button type="submit" bg="#1A2085" style={{ borderRadius: "8px", height: "fit-content" }} p={12}>
                            <IconSearch />
                        </Button>
                    </Flex>
                </form>
            </Paper>
            {!isProductDetailPage &&
                <Title mb={{ base: 12, sm: 24 }} c={COLORS.TRIOTERY_COLOR} fz={{ base: 24, sm: 32, xl: 48 }} fw={600} ta="start">
                    {isMainpage ?
                        t("section.titles.products") :
                        Content.GetTitleByLanguage(category, locale)}
                </Title>}
            {!isProductDetailPage && <Box mb={{ base: 24, sm: 40 }} c={COLORS.SECONDARY_COLOR} fz={14} fw={400} ta="start">
                {Content.HtmlRenderFromLocale({
                    titleKg: category?.contentKg,
                    titleRu: category?.contentRu,
                }, locale)}
            </Box>}
            <Grid gutter={{ base: 10, sm: 40 }} mb={{ base: 40, sm: 80 }}>
                {productsComponents}
            </Grid>
        </Box>
    )
}